import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ZAxis, Customized, LineChart, Line, Legend } from "recharts";
import { Loader2, RotateCcw, ArrowUp, ArrowDown, Bell, BellRing, Play, Pause, Clock, Circle, Square, FolderOpen, Trash2, Maximize2, X, Download, LineChart as LineChartIcon, Info, ZoomIn, ZoomOut, EyeOff, Undo2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { thesisVisualData, thesisVisualTimeline, thesisVisualHistory, alertsGet, alertSave, alertDelete, shareUpload } from "@/lib/api";
import { downloadSvgJpg, getSvgJpgBlob } from "@/lib/chartExport";
import ShareMenu from "@/components/ShareMenu";
import { addMediaItem, countMediaItems, clearMediaItems } from "@/lib/mediaLibrary";
import { ExportLibrary } from "@/components/ExportLibrary";
import { getPortfolio } from "@/lib/portfolio";
import { getWatchlistTickers } from "@/lib/storage";
import HoverTip from "@/components/HoverTip";
import PinchZoomPane from "@/components/PinchZoomPane";
import { FreshnessBadge } from "@/components/FreshnessBadge";
import { freshnessInfo } from "@/lib/freshness";
import { signalFor } from "@/lib/thresholds";
import { toast } from "sonner";
import { QUAL_TIPS } from "@/lib/qualTips";

// ---------- Helpers ----------
const MONTHS_ES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

// Next-earnings date badge. Red/bold when the report is due in ≤7 days; muted when the
// estimated date has already passed; "≈" prefix marks a tentative (estimated) date.
function EarningsBadge({ date, estimated, testid }) {
    if (!date) return <span className="text-[#9A9A9A]" data-testid={testid}>—</span>;
    const d = new Date(date);
    if (isNaN(d.getTime())) return <span className="text-[#9A9A9A]" data-testid={testid}>—</span>;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const days = Math.round((d.getTime() - today.getTime()) / 86400000);
    const sameYear = d.getUTCFullYear() === new Date().getFullYear();
    const label = `${estimated ? "≈" : ""}${d.getUTCDate()} ${MONTHS_ES[d.getUTCMonth()]}${sameYear ? "" : " '" + String(d.getUTCFullYear()).slice(2)}`;
    const imminent = days >= 0 && days <= 7;
    // "Próximos resultados" is by definition a future date → only normal or bold (≤7d).
    const weight = imminent ? 700 : 400;
    const tip = days < 0
        ? `Fecha de resultados pendiente de actualizar por Yahoo (${label}).`
        : imminent
            ? `¡Resultados ${days === 0 ? "hoy" : "en " + days + " día" + (days === 1 ? "" : "s")}! (${label})${estimated ? " · fecha estimada" : ""}`
            : `Próximos resultados${estimated ? " (estimado)" : ""}: faltan ${days} días (${label}).`;
    return (
        <HoverTip text={tip}>
            <span className="font-mono tabular-nums cursor-help whitespace-nowrap" style={{ color: "#111111", fontWeight: weight }} data-testid={testid}>
                {label}
            </span>
        </HoverTip>
    );
}

// Last published earnings date. Plain/muted (a past reference date, never colour-coded).
function LastEarningsBadge({ date, testid }) {
    if (!date) return <span className="text-[#9A9A9A]" data-testid={testid}>—</span>;
    const d = new Date(date);
    if (isNaN(d.getTime())) return <span className="text-[#9A9A9A]" data-testid={testid}>—</span>;
    const sameYear = d.getUTCFullYear() === new Date().getFullYear();
    const label = `${d.getUTCDate()} ${MONTHS_ES[d.getUTCMonth()]}${sameYear ? "" : " '" + String(d.getUTCFullYear()).slice(2)}`;
    return (
        <HoverTip text={`Últimos resultados publicados: ${label}.`}>
            <span className="font-mono tabular-nums cursor-help whitespace-nowrap text-[#4A4A4A]" data-testid={testid}>
                {label}
            </span>
        </HoverTip>
    );
}

const clamp01 = (v) => (v == null ? 0 : Math.max(0, Math.min(1, v)));
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const KPI_BETA = 0.5; // intensidad de la modulación del coeficiente KPI

/** Parte cualitativa base — media normalizada de Score y TAM Score (sin precio, sin KPI). */
const qualBase = (r) => {
    const s = clamp01((r.avg_overall_score || 0) / 100);
    const t = clamp01((r.sum_tam_score || 0) / 30);
    return (s + t) / 2;
};

/** Parte de precio — media normalizada de Ratio Compra y Venta. */
const priceBase = (r) => {
    const rc = clamp01(((r.ratio_compra_pct ?? -50) + 50) / 150);
    const rv = clamp01(((r.ratio_venta_pct ?? -50) + 50) / 150);
    return (rc + rv) / 2;
};

/** Factor KPI relativo: punto neutro = promedio de 1 (absoluto) y la media del universo.
 *  C por encima del neutro mejora el combinado; por debajo lo empeora. Sin KPI → 1 (neutro). */
const kpiFactor = (C, neutro) => {
    if (C == null || typeof C !== "number") return 1;
    return clamp(1 + KPI_BETA * (C - neutro), 0.6, 1.4);
};

/** Aguja KPI: dirección por matriz 2×2 (absoluto C↔1 = izq/dcha; relativo C↔media = arriba/abajo),
 *  inclinación 0°→45° y fuerza ∝ |C − media| normalizada (REF = mayor desviación relativa del universo). */
const kpiArrow = (C, media, REF) => {
    if (C == null || typeof C !== "number") return null;
    const rdev = C - media;   // relativo  → arriba (≥0) / abajo (<0)
    const adev = C - 1;       // absoluto  → derecha (≥0) / izquierda (<0)
    const side = adev >= 0 ? 1 : -1;
    const vert = rdev >= 0 ? 1 : -1;
    const strength = REF > 0 ? clamp(Math.abs(rdev) / REF, 0, 1) : 0;
    let color;
    if (adev >= 0 && rdev >= 0) color = "#1D7044";      // verde · arriba-dcha
    else if (adev >= 0 && rdev < 0) color = "#B8860B";  // ámbar · abajo-dcha
    else if (adev < 0 && rdev >= 0) color = "#B8860B";  // ámbar · arriba-izq
    else color = "#B32A22";                              // rojo  · abajo-izq
    return { side, vert, strength, color };
};

// Header tooltips (Score → Combinado total)
const TIP = {
    score: QUAL_TIPS.scoreCompany,
    tam: QUAL_TIPS.tamCompany,
    kpi: "Coeficiente KPI (0,5–1,5): validación operativa de la tesis con datos reales (ARR, NRR, backlog…). C = 1 + 0,5·S, donde S es la señal cuantitativa. >1 = los KPIs confirman la tesis; <1 = la refutan. Modula los combinados respecto a la media del universo. «—» = aún sin analizar en /kpis.",
    combined_qual: "Combinado cualitativo (0–100%): une el Score y el TAM Score, modulado por el coeficiente KPI (relativo a la media de todas las empresas con KPI). Mide la fuerza CUALITATIVA total — calidad + potencial + validación operativa — sin precio.",
    compra: "Ratio de Compra (%): distancia del precio actual al Precio Objetivo de Compra (POC). Positivo y alto = potencialmente barata.",
    venta: "Ratio de Venta (%): distancia del precio actual al Precio Objetivo de Venta (POV). Avisa de cuándo se agota el recorrido al alza.",
    combined: "Combinado total (0–100%): índice global que une calidad + potencial + validación KPI (vía el Combinado cualitativo) y la valoración (Compra/Venta). El coeficiente KPI cuenta una sola vez (no se dobla su peso).",
};

const fmtPct = (v) => (v == null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`);
const fmtN = (v, d = 1) => (v == null ? "—" : v.toFixed(d));
const coefColor = (c) => (c == null ? "#9ca3af" : c > 1.05 ? "#1D7044" : c < 0.95 ? "#B32A22" : "#B8860B");

// ---------- Quadrant background labels (with subtitle) ----------
const QuadrantLabels = () => (
    <>
        {/* TL: low score + alto ratio compra (barata relativa) → trampa */}
        <text x="12%" y="10%" textAnchor="start" fill="#B8860B" fillOpacity={0.55} fontSize={14} fontWeight={700}>⚠️ TRAMPA DE VALOR</text>
        <text x="12%" y="10%" dy={14} textAnchor="start" fill="#B8860B" fillOpacity={0.55} fontSize={10} fontStyle="italic">Calidad baja + barata → auditar bien antes</text>

        {/* TR: high score + alto ratio compra → joya */}
        <text x="88%" y="10%" textAnchor="end" fill="#1D7044" fillOpacity={0.65} fontSize={14} fontWeight={700}>🏆 JOYAS ESCONDIDAS</text>
        <text x="88%" y="10%" dy={14} textAnchor="end" fill="#1D7044" fillOpacity={0.65} fontSize={10} fontStyle="italic">Calidad alta + descuento → comprar</text>

        {/* BL: low score + bajo ratio compra (cara) → sobrevalorada */}
        <text x="12%" y="80%" textAnchor="start" fill="#B32A22" fillOpacity={0.55} fontSize={14} fontWeight={700}>🚫 SOBREVALORADA</text>
        <text x="12%" y="80%" dy={14} textAnchor="start" fill="#B32A22" fillOpacity={0.55} fontSize={10} fontStyle="italic">Calidad baja + cara → ignorar</text>

        {/* BR: high score + bajo ratio compra → premium, esperar */}
        <text x="88%" y="80%" textAnchor="end" fill="#4A4A4A" fillOpacity={0.65} fontSize={14} fontWeight={700}>💎 PREMIUM</text>
        <text x="88%" y="80%" dy={14} textAnchor="end" fill="#4A4A4A" fillOpacity={0.65} fontSize={10} fontStyle="italic">Calidad alta + cara → esperar entrada</text>
    </>
);

// ---------- Custom dot ----------
// Colour is driven by the user's Ratio Venta thresholds (Umbrales cara/justa/barata)
// so that the visual quadrant matches the rest of the app (portfolio, company page).
// The chart subscribes to `vs:thresholds-changed` so when the user opens the dialog
// and edits cheap/fair, the dots re-paint immediately without a full reload.
const colorForRv = (rv) => signalFor(rv, "venta").color;

const DEG = Math.PI / 180;
// KPI needle: radial arrow emerging from the dot's edge. Angle measured from the
// center; tilt (0–45°) + length scale with relative strength. Direction & colour
// follow the 2×2 matrix (absolute C↔1 = left/right, relative C↔mean = up/down).
const KpiNeedle = ({ cx, cy, r, arrow }) => {
    const tilt = 45 * arrow.strength * DEG;
    const dirX = arrow.side * Math.cos(tilt);
    const dirY = -arrow.vert * Math.sin(tilt); // screen y is inverted (up = negative)
    const len = 11 + 30 * arrow.strength;
    const x0 = cx + r * dirX, y0 = cy + r * dirY;
    const x1 = cx + (r + len) * dirX, y1 = cy + (r + len) * dirY;
    const ang = Math.atan2(dirY, dirX);
    const ux = Math.cos(ang), uy = Math.sin(ang);
    const px = -uy, py = ux;
    const h = 7, w = 4;
    const bx = x1 - h * ux, by = y1 - h * uy;
    return (
        <g pointerEvents="none">
            <line x1={x0} y1={y0} x2={bx} y2={by} stroke={arrow.color} strokeWidth={2.6} strokeLinecap="round" />
            <polygon points={`${x1},${y1} ${bx + w * px},${by + w * py} ${bx - w * px},${by - w * py}`} fill={arrow.color} stroke="#000" strokeWidth={0.5} />
        </g>
    );
};

// How the last press on a bubble was made: a mouse click hides the company at once,
// a tap (touch or pen) selects it and the bar under the chart offers «Ocultar» — on a
// phone the tap is also how the tooltip opens, and in full screen how the zoom works.
let lastPointerType = "mouse";

const Dot = (props) => {
    const { cx, cy, payload, onPick, pickedTk } = props;
    const picked = !!pickedTk && payload.ticker === pickedTk;
    const r = Math.max(5, Math.min(22, 4 + Math.sqrt(Math.max(0, payload.sum_tam_score || 0)) * 2.2));
    return (
        <g
            style={onPick ? { cursor: "pointer" } : undefined}
            onPointerDown={onPick ? (e) => { lastPointerType = e.pointerType || "mouse"; } : undefined}
            onClick={onPick ? (e) => { e.stopPropagation(); onPick(payload.ticker, lastPointerType); } : undefined}
            data-testid={`visual-dot-${payload.ticker}`}
        >
            {picked && <circle cx={cx} cy={cy} r={r + 5} fill="none" stroke="#000" strokeWidth={2} strokeDasharray="3 2" pointerEvents="none" />}
            <circle cx={cx} cy={cy} r={r} fill={colorForRv(payload.ratio_venta_pct)} fillOpacity={0.75} stroke="#000" strokeWidth={picked ? 2.5 : 1} />
            <text x={cx} y={cy + 4} textAnchor="middle" fontSize={9} fontWeight={700} fill="#fff" pointerEvents="none">{payload.ticker}</text>
            {payload.kpi_arrow && <KpiNeedle cx={cx} cy={cy} r={r} arrow={payload.kpi_arrow} />}
        </g>
    );
};

// ---------- Timeline helpers ----------
const monthLabel = (m) => {
    if (!m) return "";
    const [y, mo] = m.split("-").map(Number);
    return `${MONTHS_ES[mo - 1]} '${String(y).slice(2)}`;
};

// Day-precision label ("5 ene '26") for the per-company classic history chart.
// Accepts a bare "YYYY-MM-DD", a full ISO timestamp, or an epoch-ms number (the chart's
// real time-scale X axis) — points recorded the same day get distinct timestamps so they
// don't collapse into one, but still share a label.
const dayLabel = (v) => {
    if (v == null || v === "") return "";
    const iso = typeof v === "number" ? new Date(v).toISOString() : v;
    const [y, mo, d] = iso.slice(0, 10).split("-").map(Number);
    return `${d} ${MONTHS_ES[mo - 1]} '${String(y).slice(2)}`;
};

// Trail layer: draws each visible company's path through the quadrant up to the
// current step. Rendered via recharts <Customized> so we can use the live axis
// scales (xAxisMap/yAxisMap) to map data coords → pixels.
const TrailLayer = (props) => {
    const { xAxisMap, yAxisMap, trails } = props;
    if (!trails || !xAxisMap || !yAxisMap) return null;
    const xa = Object.values(xAxisMap)[0];
    const ya = Object.values(yAxisMap)[0];
    if (!xa?.scale || !ya?.scale) return null;
    const sx = xa.scale, sy = ya.scale;
    return (
        <g pointerEvents="none">
            {trails.map((tr) => {
                if (!tr.path || tr.path.length < 2) return null;
                const pts = tr.path.map((p) => `${sx(p.x)},${sy(p.y)}`).join(" ");
                const last = tr.path[tr.path.length - 1];
                const first = tr.path[0];
                return (
                    <g key={tr.ticker}>
                        <polyline points={pts} fill="none" stroke={tr.color} strokeOpacity={0.6} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
                        {tr.path.map((p, i) => (
                            <circle key={i} cx={sx(p.x)} cy={sy(p.y)} r={2.2} fill={tr.color} fillOpacity={0.55} />
                        ))}
                        {/* hollow marker at the start of the path (oldest point) */}
                        <circle cx={sx(first.x)} cy={sy(first.y)} r={3.5} fill="#fff" stroke={tr.color} strokeWidth={1.5} fillOpacity={0.9} />
                    </g>
                );
            })}
        </g>
    );
};


const canHover = () => {
    try { return window.matchMedia("(hover: hover) and (pointer: fine)").matches; } catch { return true; }
};

const ScatterTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload;
    return (
        <div className="bg-white border border-black p-3 text-xs font-mono shadow-lg" style={{ minWidth: 220 }}>
            <div className="font-semibold text-black text-sm mb-1">{p.ticker}</div>
            <div className="text-[#4A4A4A] text-[11px] mb-2 font-sans">{p.name}</div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                <div className="text-[#4A4A4A]">Score</div><div className="text-right">{fmtN(p.avg_overall_score)}</div>
                <div className="text-[#4A4A4A]">TAM Score</div><div className="text-right">{fmtN(p.sum_tam_score, 2)}</div>
                <div className="text-[#4A4A4A]">Ratio Compra</div><div className="text-right">{fmtPct(p.ratio_compra_pct)}</div>
                <div className="text-[#4A4A4A]">Ratio Venta</div><div className="text-right">{fmtPct(p.ratio_venta_pct)}</div>
                {typeof p.kpi_coef === "number" && (<>
                    <div className="text-[#4A4A4A]">Coef KPI</div><div className="text-right" style={{ color: coefColor(p.kpi_coef) }}>{p.kpi_coef.toFixed(2)}</div>
                </>)}
                {typeof p.combined === "number" && (<>
                    <div className="text-[#4A4A4A] border-t border-black/10 pt-1 mt-1">Combinado</div><div className="text-right border-t border-black/10 pt-1 mt-1 font-semibold">{(p.combined * 100).toFixed(1)}%</div>
                </>)}
            </div>
            <div className="mt-2 pt-1.5 border-t border-black/10 text-[10px] text-[#7A7A7A] font-sans">
                {canHover() ? "Clic en la burbuja para quitarla del mapa" : "«Ocultar» debajo del gráfico la quita del mapa"}
            </div>
        </div>
    );
};

// ---------- View memory ----------
// The view (checked companies, filters, table order, timeline mode, scroll) survives going to
// another page and coming back in the same browser tab. sessionStorage, wrapped: it can be
// unavailable (private mode) and the page must work without it.
const VIEW_KEY = "vs.visual.view.v1";
const loadView = () => {
    try { return JSON.parse(window.sessionStorage.getItem(VIEW_KEY) || "null") || {}; } catch { return {}; }
};
const saveView = (patch) => {
    try { window.sessionStorage.setItem(VIEW_KEY, JSON.stringify({ ...loadView(), ...patch })); } catch { /* ignore */ }
};
const DEFAULT_FILTERS = { score: 0, tam: 0, kpi: -10000, rc: -10000, rv: -10000, cqual: 0, ctotal: 0, level: "both", sector: "all" };

// ---------- Page ----------
export default function Visual() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(new Set()); // all unchecked by default
    const savedView = useRef(loadView());             // what this tab had last time (read once)
    const restorePending = useRef(Array.isArray(savedView.current.selected));
    const [sortKey, setSortKey] = useState(() => savedView.current.sortKey || "combined");
    const [sortDir, setSortDir] = useState(() => savedView.current.sortDir || "desc");
    const [kpiMean, setKpiMean] = useState(null);
    const [noKpiCount, setNoKpiCount] = useState(0);
    const [alerts, setAlerts] = useState({});  // ticker -> alert config
    const [historyRow, setHistoryRow] = useState(null); // {ticker, name} → opens the classic history modal
    // Companies taken off the map from the chart itself (most recent last): hiding one
    // unchecks it in the table, so axes and medians re-fit to the rest; the bar under
    // the chart brings them back one by one, the last one («Deshacer») or all.
    const [mapHidden, setMapHidden] = useState(() => (Array.isArray(savedView.current.mapHidden) ? savedView.current.mapHidden : []));
    const [pickedTk, setPickedTk] = useState(null); // tapped bubble (touch): offers «Ocultar»

    // --- Timeline (time dial) ---
    const [tlMode, setTlMode] = useState(false);
    const [tl, setTl] = useState(null);          // { months, current_month, series }
    const [tlLoading, setTlLoading] = useState(false);
    const [tlErr, setTlErr] = useState(null);
    const [tlIdx, setTlIdx] = useState(0);
    const [tlPlaying, setTlPlaying] = useState(false);
    const [tlTrail, setTlTrail] = useState(() => savedView.current.tlTrail ?? true);
    const [tlSpeed, setTlSpeed] = useState(() => savedView.current.tlSpeed || 600); // ms per step

    // --- Recording / export library ---
    const chartRef = useRef(null);
    const recCancelRef = useRef(false);
    const [recArmed, setRecArmed] = useState(false);
    const [recording, setRecording] = useState(false);
    const [clipCount, setClipCount] = useState(0);
    const [libOpen, setLibOpen] = useState(false);
    const [chartFull, setChartFull] = useState(false);   // fullscreen chart overlay (manual "Ampliar")
    const fullChartRef = useRef(null);
    const [jpgBusy, setJpgBusy] = useState(false);
    const downloadJpg = useCallback(async () => {
        setJpgBusy(true);
        try { await downloadSvgJpg(fullChartRef.current, "cuadrante-vegavalor"); }
        catch { const { toast } = await import("sonner"); toast.error("No se pudo exportar el gráfico"); }
        finally { setJpgBusy(false); }
    }, []);
    useEffect(() => { countMediaItems("timeline-clip").then(setClipCount).catch(() => {}); }, []);

    // Lock body scroll + allow ESC to close while the fullscreen chart is open.
    useEffect(() => {
        if (!chartFull) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (e) => { if (e.key === "Escape") setChartFull(false); };
        window.addEventListener("keydown", onKey);
        return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
    }, [chartFull]);

    const enableTimeline = useCallback(async () => {
        if (tl) { setTlMode(true); return; }
        setTlLoading(true); setTlErr(null);
        try {
            const d = await thesisVisualTimeline();
            setTl(d);
            setTlIdx(Math.max(0, (d.months || []).length - 1));
            setTlMode(true);
        } catch (e) {
            setTlErr(e?.response?.data?.detail || e.message || "No se pudo cargar la línea de tiempo");
        } finally { setTlLoading(false); }
    }, [tl]);

    // Playback loop
    useEffect(() => {
        if (!tlPlaying || !tl) return;
        const last = (tl.months || []).length - 1;
        if (tlIdx >= last) { setTlPlaying(false); return; }
        const id = setTimeout(() => setTlIdx((i) => Math.min(last, i + 1)), tlSpeed);
        return () => clearTimeout(id);
    }, [tlPlaying, tlIdx, tl, tlSpeed]);

    // Rasterize the live chart SVG onto a canvas (one frame).
    const drawFrame = useCallback((canvas, ctx) => new Promise((resolve, reject) => {
        const svg = chartRef.current && chartRef.current.querySelector("svg");
        if (!svg) return reject(new Error("no-svg"));
        const rect = svg.getBoundingClientRect();
        const clone = svg.cloneNode(true);
        clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
        clone.setAttribute("width", rect.width);
        clone.setAttribute("height", rect.height);
        const xml = new XMLSerializer().serializeToString(clone);
        const img = new Image();
        img.onload = () => {
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve();
        };
        img.onerror = reject;
        img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(xml);
    }), []);

    // Record a full playthrough of the time dial into a WebM clip (canvas capture).
    const recordPlaythrough = useCallback(async () => {
        if (!tl || !chartRef.current) return;
        const svg = chartRef.current.querySelector("svg");
        if (!svg || !window.MediaRecorder) { toast.error("La grabación no está disponible en este navegador"); return; }
        const rect = svg.getBoundingClientRect();
        const scale = 1.75;  // supersample for sharper output
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(rect.width * scale); canvas.height = Math.round(rect.height * scale);
        const ctx = canvas.getContext("2d");
        // Prefer MP4 (H.264) — universal & higher quality — then fall back to WebM.
        const candidates = ["video/mp4;codecs=avc1.42E01E", "video/mp4", "video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"];
        const mime = candidates.find((m) => { try { return MediaRecorder.isTypeSupported(m); } catch { return false; } }) || "video/webm";
        const baseMime = mime.startsWith("video/mp4") ? "video/mp4" : "video/webm";
        const stream = canvas.captureStream(0);
        const vtrack = stream.getVideoTracks()[0];
        const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 8000000 });
        const chunks = [];
        rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
        const stopped = new Promise((res) => { rec.onstop = res; });
        setTlPlaying(false);
        setRecording(true);
        recCancelRef.current = false;
        const fps = tlSpeed <= 250 ? 12 : (tlSpeed <= 600 ? 8 : 5);
        const frameMs = 1000 / fps;
        const wait2raf = () => new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
        const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
        rec.start();
        try {
            const N = tl.months.length;
            for (let i = 0; i < N; i++) {
                if (recCancelRef.current) break;
                setTlIdx(i);
                await wait2raf();
                await drawFrame(canvas, ctx);
                if (vtrack.requestFrame) vtrack.requestFrame();
                await sleep(frameMs);
            }
            await drawFrame(canvas, ctx);
            if (vtrack.requestFrame) vtrack.requestFrame();
            await sleep(600);
        } catch (e) {
            toast.error("Error durante la grabación");
        } finally {
            rec.stop();
            await stopped;
            setRecording(false);
        }
        if (recCancelRef.current || !chunks.length) { toast("Grabación cancelada"); return; }
        try {
            const blob = new Blob(chunks, { type: baseMime });
            const thumb = canvas.toDataURL("image/png");
            const now = new Date();
            const name = `Recorrido ${now.toLocaleDateString("es-ES")} ${now.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}`;
            await addMediaItem({ kind: "timeline-clip", name, mime: baseMime, blob, thumbnail: thumb, meta: { months: tl.months.length } });
            setClipCount(await countMediaItems("timeline-clip"));
            toast.success("Recorrido grabado ✓");
        } catch (e) { toast.error("No se pudo guardar el clip"); }
    }, [tl, tlSpeed, drawFrame]);

    const onTimelinePlay = useCallback(() => {
        if (recording) { recCancelRef.current = true; return; }   // acts as Stop
        if (recArmed) { setTlIdx(0); recordPlaythrough(); return; }
        if (!tl) return;
        if (tlIdx >= tl.months.length - 1) setTlIdx(0);
        setTlPlaying((p) => !p);
    }, [recording, recArmed, tl, tlIdx, recordPlaythrough]);

    const clearClips = useCallback(async () => {
        const n = await clearMediaItems("timeline-clip");
        setClipCount(0);
        toast.success(`${n} clip(s) borrados`);
    }, []);


    useEffect(() => {
        if (!user) { setAlerts({}); return; }
        alertsGet().then((d) => setAlerts(d.alerts || {})).catch(() => { });
    }, [user]);

    const onAlertSaved = useCallback((ticker, alert) => {
        setAlerts((prev) => {
            const next = { ...prev };
            if (alert) next[ticker] = alert; else delete next[ticker];
            return next;
        });
    }, []);

    // Filters (only affect map visibility, not the table)
    // level: "both" | "n1" (Cartera) | "n2" (Seguimiento); sector: "all" | a sector of the rows.
    const [filters, setFilters] = useState(() => ({ ...DEFAULT_FILTERS, ...(savedView.current.filters || {}) }));

    // Ticker membership of Nivel 1 (Cartera) and Nivel 2 (Seguimiento), read from
    // local storage (kept in sync with the cloud by WatchlistCloudSync).
    const [n1Set, setN1Set] = useState(() => new Set());
    const [n2Set, setN2Set] = useState(() => new Set());
    useEffect(() => {
        const load = () => {
            setN1Set(new Set(getPortfolio().map((p) => (p.ticker || "").toUpperCase())));
            setN2Set(new Set(getWatchlistTickers().map((t) => (t || "").toUpperCase())));
        };
        load();
        window.addEventListener("vs:portfolio-changed", load);
        window.addEventListener("vs:watchlist-changed", load);
        return () => {
            window.removeEventListener("vs:portfolio-changed", load);
            window.removeEventListener("vs:watchlist-changed", load);
        };
    }, []);

    // Load on mount (and whenever user changes). All rows start SELECTED so the
    // map shows everything immediately — user uncheck to narrow it down.
    const reload = useCallback(async () => {
        if (!user) { setRows([]); setSelected(new Set()); return; }
        setLoading(true); setError(null);
        try {
            const d = await thesisVisualData();
            const raw = d.rows || [];
            // Media de coef KPI sobre TODAS las empresas que tienen KPI (las demás no cuentan).
            const coefs = raw.map((r) => r.kpi_coef).filter((v) => typeof v === "number");
            const mediaC = coefs.length ? coefs.reduce((a, b) => a + b, 0) / coefs.length : 1;
            const neutro = (1 + mediaC) / 2;
            const REF = coefs.length ? Math.max(...coefs.map((c) => Math.abs(c - mediaC))) : 0;
            const enriched = raw.map((r) => {
                const f = kpiFactor(r.kpi_coef, neutro);
                const cq = clamp01(qualBase(r) * f);
                return { ...r, kpi_factor: f, kpi_arrow: kpiArrow(r.kpi_coef, mediaC, REF), combined_qual: cq, combined: (cq + priceBase(r)) / 2 };
            });
            setRows(enriched);
            setKpiMean(coefs.length ? mediaC : null);
            setNoKpiCount(raw.length - coefs.length);
            setSelected(new Set(enriched.map((r) => r.ticker)));
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => { reload(); }, [reload]);

    // Live-update dot colours when the user edits the cheap/fair thresholds.
    // ThresholdsDialog dispatches `vs:thresholds-changed`; we bump a tick to force
    // recharts to re-render the Scatter dots (signalFor reads localStorage on demand).
    const [, setThresholdsTick] = useState(0);
    useEffect(() => {
        const onChange = () => setThresholdsTick((n) => n + 1);
        window.addEventListener("vs:thresholds-changed", onChange);
        return () => window.removeEventListener("vs:thresholds-changed", onChange);
    }, []);

    // A row passes the numeric filters (drives both map visibility and table marking).
    const passesFilters = useCallback((r, f) => {
        const lvl = f.level || "both";
        const tk = (r.ticker || "").toUpperCase();
        if (lvl === "n1" && !n1Set.has(tk)) return false;
        if (lvl === "n2" && !n2Set.has(tk)) return false;
        if (f.sector && f.sector !== "all" && (r.sector || "Sin clasificar") !== f.sector) return false;
        return (
            (r.avg_overall_score || 0) >= f.score &&
            (r.sum_tam_score || 0) >= f.tam &&
            (typeof r.kpi_coef === "number" ? r.kpi_coef : -10000) >= f.kpi &&
            (r.ratio_compra_pct ?? -10000) >= f.rc &&
            (r.ratio_venta_pct ?? -10000) >= f.rv &&
            ((r.combined_qual ?? 0) * 100) >= f.cqual &&
            ((r.combined ?? 0) * 100) >= f.ctotal
        );
    }, [n1Set, n2Set]);

    // Keep the table checkboxes in sync with the filters: selecting/adjusting a filter
    // automatically marks (passes) or unmarks (fails) each row.
    useEffect(() => {
        if (!rows.length) return;
        if (restorePending.current) {
            // Coming back to the page: the companies checked by hand last time, plus any new
            // company (not in the table then) that passes the filters.
            restorePending.current = false;
            const known = new Set(savedView.current.known || []);
            const had = new Set(savedView.current.selected || []);
            setSelected(new Set(rows.filter((r) => (known.has(r.ticker) ? had.has(r.ticker) : passesFilters(r, filters))).map((r) => r.ticker)));
            return;
        }
        setSelected(new Set(rows.filter((r) => passesFilters(r, filters)).map((r) => r.ticker)));
    }, [filters, rows, passesFilters]);

    // Remember the view as it changes (only once the table has loaded, so the empty
    // first render never overwrites what this tab had).
    useEffect(() => {
        if (!rows.length || restorePending.current) return;
        saveView({ selected: [...selected], known: rows.map((r) => r.ticker), sortKey, sortDir, filters, tlMode, tlTrail, tlSpeed, mapHidden });
    }, [rows, selected, sortKey, sortDir, filters, tlMode, tlTrail, tlSpeed, mapHidden]);

    // Scroll position: saved while scrolling and restored once the table is back.
    useEffect(() => {
        let t = null;
        const onScroll = () => { clearTimeout(t); t = setTimeout(() => saveView({ scrollY: window.scrollY }), 150); };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => { clearTimeout(t); window.removeEventListener("scroll", onScroll); };
    }, []);
    const scrollRestored = useRef(false);
    useEffect(() => {
        if (scrollRestored.current || loading || !rows.length) return;
        scrollRestored.current = true;
        const y = savedView.current.scrollY;
        if (y > 0) requestAnimationFrame(() => window.scrollTo(0, y));
        if (savedView.current.tlMode) enableTimeline();  // the timeline view was open
    }, [loading, rows.length, enableTimeline]);

    // Sorting (table) — always over ALL rows. String columns (ticker/name) sort
    // alphabetically; the rest numerically.
    const STRING_KEYS = useMemo(() => new Set(["ticker", "name"]), []);
    const DATE_KEYS = useMemo(() => new Set(["thesis_updated_at", "next_earnings_date", "last_earnings_date"]), []);
    const sortedRows = useMemo(() => {
        const arr = [...rows];
        const isStr = STRING_KEYS.has(sortKey);
        const isDate = DATE_KEYS.has(sortKey);
        arr.sort((a, b) => {
            const av = a[sortKey]; const bv = b[sortKey];
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            if (isStr) {
                const c = String(av).localeCompare(String(bv), "es", { sensitivity: "base" });
                return sortDir === "asc" ? c : -c;
            }
            if (isDate) {
                const at = Date.parse(av); const bt = Date.parse(bv);
                return sortDir === "asc" ? at - bt : bt - at;
            }
            return sortDir === "asc" ? av - bv : bv - av;
        });
        // Selected rows (the ones the filters actually left checked, visible on the map)
        // float to the top as a group — visually easier to scan together — while keeping
        // the chosen column's order WITHIN each group. Re-sorting by a column reorders
        // both groups by that column but never moves a row across the selected/unselected
        // boundary; only the filters (which drive `selected`) do that.
        const sel = [], unsel = [];
        for (const r of arr) (selected.has(r.ticker) ? sel : unsel).push(r);
        return [...sel, ...unsel];
    }, [rows, sortKey, sortDir, STRING_KEYS, DATE_KEYS, selected]);

    const onSort = (k) => {
        if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else { setSortKey(k); setSortDir(STRING_KEYS.has(k) ? "asc" : "desc"); }
    };

    const allSelected = rows.length > 0 && selected.size === rows.length;
    const toggleAll = () => {
        if (allSelected) setSelected(new Set());
        else setSelected(new Set(rows.map((r) => r.ticker)));
    };
    const toggleOne = (tk) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(tk)) next.delete(tk); else next.add(tk);
            return next;
        });
    };

    const hideFromMap = (tk) => {
        setSelected((prev) => { const next = new Set(prev); next.delete(tk); return next; });
        setMapHidden((prev) => [...prev.filter((t) => t !== tk), tk]);
        setPickedTk(null);
    };
    const restoreToMap = (tks) => {
        const list = Array.isArray(tks) ? tks : [tks];
        setSelected((prev) => { const next = new Set(prev); list.forEach((t) => next.add(t)); return next; });
        setMapHidden((prev) => prev.filter((t) => !list.includes(t)));
    };
    // Still hidden = taken off from the chart and not checked again in the table since.
    const hiddenOnMap = useMemo(() => mapHidden.filter((t) => !selected.has(t) && rows.some((r) => r.ticker === t)), [mapHidden, selected, rows]);
    const onDotPick = useCallback((tk, pointerType) => {
        if (pointerType === "mouse") hideFromMap(tk);
        else setPickedTk((cur) => (cur === tk ? null : tk));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => { if (pickedTk && !selected.has(pickedTk)) setPickedTk(null); }, [selected, pickedTk]);

    // Map data: selected rows that also pass the filters (need both axes to plot).
    const mapRows = useMemo(() => rows.filter((r) => {
        if (!selected.has(r.ticker)) return false;
        if (r.avg_overall_score == null || r.ratio_compra_pct == null) return false; // need both axes
        return passesFilters(r, filters);
    }), [rows, selected, filters, passesFilters]);

    const resetFilters = () => setFilters({ ...DEFAULT_FILTERS });

    // Sectors present in the loaded universe, alphabetical — same field/labels
    // Portfolio and Watchlist's sector donuts already use (Yahoo Finance's own taxonomy).
    const sectorOptions = useMemo(() => {
        const set = new Set(rows.map((r) => r.sector || "Sin clasificar"));
        return Array.from(set).sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" }));
    }, [rows]);

    // Dynamic quadrant divider: use median of MAP rows so dots end up balanced
    // across the 4 quadrants regardless of dataset spread. Falls back to median
    // of all rows when nothing is selected, then to (50, 0) when no data at all.
    const { medianX, medianY } = useMemo(() => {
        const base = mapRows.length ? mapRows : rows.filter((r) => r.avg_overall_score != null && r.ratio_compra_pct != null);
        if (!base.length) return { medianX: 50, medianY: 0 };
        const median = (arr) => {
            const s = [...arr].sort((a, b) => a - b);
            const m = Math.floor(s.length / 2);
            return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
        };
        return {
            medianX: median(base.map((r) => r.avg_overall_score)),
            medianY: median(base.map((r) => r.ratio_compra_pct)),
        };
    }, [mapRows, rows]);

    // Dynamic X-axis domain: start at 60 by default (most quality companies score >60,
    // and 60 is also the threshold above which we consider a thesis "investable"). If
    // any visible company has score <60, extend the axis downward to include it.
    const xDomain = useMemo(() => {
        const base = mapRows.length ? mapRows : rows;
        const scores = base.map((r) => r.avg_overall_score).filter((v) => v != null);
        if (!scores.length) return [60, 100];
        const minScore = Math.min(...scores);
        if (minScore >= 60) return [60, 100];
        return [Math.max(0, Math.floor(minScore / 5) * 5), 100];
    }, [mapRows, rows]);

    // --- Timeline derived data ---
    const tlLevelOk = useCallback((tk) => {
        const lvl = filters.level || "both";
        const T = (tk || "").toUpperCase();
        if (lvl === "n1") return n1Set.has(T);
        if (lvl === "n2") return n2Set.has(T);
        return true;
    }, [filters.level, n1Set, n2Set]);

    const tlSeriesVisible = useMemo(() => {
        if (!tl) return [];
        return (tl.series || []).filter((s) => selected.has(s.ticker) && tlLevelOk(s.ticker));
    }, [tl, selected, tlLevelOk]);

    const tlDomains = useMemo(() => {
        if (!tlSeriesVisible.length) return { x: [60, 100], y: [-50, 50] };
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        for (const s of tlSeriesVisible) {
            for (const m in s.pts) {
                const a = s.pts[m];
                if (a[0] != null) { minX = Math.min(minX, a[0]); maxX = Math.max(maxX, a[0]); }
                if (a[2] != null) { minY = Math.min(minY, a[2]); maxY = Math.max(maxY, a[2]); }
            }
        }
        const padY = (maxY - minY) * 0.08 || 10;
        return { x: [Math.min(60, Math.floor(minX / 5) * 5), 100], y: [Math.floor(minY - padY), Math.ceil(maxY + padY)] };
    }, [tlSeriesVisible]);

    const tlStepRows = useMemo(() => {
        if (!tl || !tl.months.length) return [];
        const month = tl.months[Math.min(tlIdx, tl.months.length - 1)];
        const raw = [];
        for (const s of tlSeriesVisible) {
            const a = s.pts[month];
            if (!a || a[0] == null || a[2] == null) continue;
            raw.push({
                ticker: s.ticker, name: s.name,
                avg_overall_score: a[0], sum_tam_score: a[1],
                ratio_compra_pct: a[2], ratio_venta_pct: a[3],
                kpi_coef: (typeof a[4] === "number" ? a[4] : null),
            });
        }
        const coefs = raw.map((r) => r.kpi_coef).filter((v) => typeof v === "number");
        const media = coefs.length ? coefs.reduce((x, y) => x + y, 0) / coefs.length : 1;
        const REF = coefs.length ? Math.max(...coefs.map((c) => Math.abs(c - media))) : 0;
        const neutro = (1 + media) / 2;
        return raw.map((r) => {
            const f = kpiFactor(r.kpi_coef, neutro);
            const cq = clamp01(qualBase(r) * f);
            return { ...r, kpi_arrow: kpiArrow(r.kpi_coef, media, REF), combined_qual: cq, combined: (cq + priceBase(r)) / 2 };
        });
    }, [tl, tlIdx, tlSeriesVisible]);

    // Quadrant dividers fixed at PRESENT medians so dots visibly move across them.
    const tlMedians = useMemo(() => {
        if (!tl || !tlSeriesVisible.length) return { mx: 50, my: 0 };
        const cur = tl.current_month;
        const med = (arr) => { const s = [...arr].sort((a, b) => a - b); const m = Math.floor(s.length / 2); return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };
        const xs = [], ys = [];
        for (const s of tlSeriesVisible) { const a = s.pts[cur]; if (a) { if (a[0] != null) xs.push(a[0]); if (a[2] != null) ys.push(a[2]); } }
        return { mx: xs.length ? med(xs) : 50, my: ys.length ? med(ys) : 0 };
    }, [tl, tlSeriesVisible]);

    const tlTrails = useMemo(() => {
        if (!tl || !tlTrail) return [];
        const upto = tl.months.slice(0, tlIdx + 1);
        return tlSeriesVisible.map((s) => {
            const path = [];
            for (const m of upto) { const a = s.pts[m]; if (a && a[0] != null && a[2] != null) path.push({ x: a[0], y: a[2] }); }
            const cur = s.pts[tl.months[Math.min(tlIdx, tl.months.length - 1)]];
            return { ticker: s.ticker, path, color: colorForRv(cur ? cur[3] : 0) };
        }).filter((t) => t.path.length >= 2);
    }, [tl, tlIdx, tlSeriesVisible, tlTrail]);


    if (!user) {
        return (
            <div className="max-w-4xl mx-auto px-6 py-12 text-center">
                <div className="text-xl font-serif text-[#4A4A4A]">Inicia sesión para ver tu Visual.</div>
            </div>
        );
    }

    const chartNode = (
        <ScatterChart margin={{ top: 20, right: 30, bottom: 40, left: 50 }}>
            <CartesianGrid stroke="#00000010" />
            <XAxis type="number" dataKey="avg_overall_score" name="Score" domain={tlMode ? tlDomains.x : xDomain} allowDataOverflow tick={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} label={{ value: "Score cualitativo →", position: "insideBottom", offset: -10, fontSize: 11, fontFamily: "IBM Plex Mono" }} />
            <YAxis type="number" dataKey="ratio_compra_pct" name="Ratio Compra %" domain={tlMode ? tlDomains.y : ["auto", "auto"]} allowDataOverflow tick={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} label={{ value: "Ratio Compra % →", angle: -90, position: "insideLeft", fontSize: 11, fontFamily: "IBM Plex Mono" }} />
            <ZAxis dataKey="sum_tam_score" range={[60, 600]} />
            <Tooltip content={<ScatterTooltip />} />
            <ReferenceLine x={tlMode ? tlMedians.mx : medianX} stroke="#000" strokeDasharray="3 3" label={{ value: `Score ≈${(tlMode ? tlMedians.mx : medianX).toFixed(0)}`, position: "top", fill: "#4A4A4A", fontSize: 10, fontFamily: "IBM Plex Mono" }} />
            <ReferenceLine y={tlMode ? tlMedians.my : medianY} stroke="#000" strokeDasharray="3 3" label={{ value: `${(tlMode ? tlMedians.my : medianY) >= 0 ? "+" : ""}${(tlMode ? tlMedians.my : medianY).toFixed(0)}%`, position: "right", fill: "#4A4A4A", fontSize: 10, fontFamily: "IBM Plex Mono" }} />
            <QuadrantLabels />
            {tlMode && tlTrail && <Customized component={(p) => <TrailLayer {...p} trails={tlTrails} />} />}
            <Scatter data={tlMode ? tlStepRows : mapRows} shape={<Dot onPick={onDotPick} pickedTk={pickedTk} />} isAnimationActive={!tlMode || tlTrail} animationDuration={350} animationEasing="ease-out" />
        </ScatterChart>
    );

    // Under the chart (page and full screen): the tapped bubble's «Ocultar» and the
    // companies taken off the map, each one a chip that brings it back.
    const pickedRow = pickedTk ? rows.find((r) => r.ticker === pickedTk) : null;
    const hiddenBar = (pickedRow || hiddenOnMap.length > 0) ? (
        <div className="mt-2 flex flex-col gap-2" data-testid="visual-map-hidden-bar">
            {pickedRow && (
                <div className="flex items-center gap-2 flex-wrap border border-black bg-[#F5E4D4] px-3 py-2" data-testid="visual-map-picked">
                    <span className="text-xs min-w-0"><strong className="font-mono">{pickedRow.ticker}</strong> <span className="text-[#4A4A4A]">{pickedRow.name}</span></span>
                    <span className="ml-auto flex items-center gap-2">
                        <button onClick={() => hideFromMap(pickedRow.ticker)} className="overline text-xs px-3 py-1.5 bg-black text-white hover:bg-[#052049] transition flex items-center gap-1.5" data-testid="visual-map-hide-picked">
                            <EyeOff size={12} /> Ocultar del mapa
                        </button>
                        <button onClick={() => setPickedTk(null)} className="text-[#4A4A4A] hover:text-black p-1" aria-label="Cancelar" data-testid="visual-map-pick-cancel"><X size={14} /></button>
                    </span>
                </div>
            )}
            {hiddenOnMap.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap text-xs" data-testid="visual-map-hidden">
                    <span className="text-[#4A4A4A] mr-1">Ocultas del mapa:</span>
                    {hiddenOnMap.map((tk) => (
                        <button key={tk} onClick={() => restoreToMap(tk)} title={`Volver a mostrar ${tk}`}
                                className="inline-flex items-center gap-1 font-mono border border-black/40 px-2 py-0.5 hover:border-black hover:bg-black hover:text-white transition"
                                data-testid={`visual-map-restore-${tk}`}>
                            {tk} <X size={11} />
                        </button>
                    ))}
                    <span className="flex items-center gap-1.5 ml-auto">
                        <button onClick={() => restoreToMap(hiddenOnMap[hiddenOnMap.length - 1])}
                                className="overline text-xs px-2.5 py-1 border border-black hover:bg-black hover:text-white transition flex items-center gap-1"
                                data-testid="visual-map-undo">
                            <Undo2 size={12} /> Deshacer
                        </button>
                        {hiddenOnMap.length > 1 && (
                            <button onClick={() => restoreToMap(hiddenOnMap)}
                                    className="overline text-xs px-2.5 py-1 border border-black hover:bg-black hover:text-white transition"
                                    data-testid="visual-map-restore-all">
                                Mostrar todas
                            </button>
                        )}
                    </span>
                </div>
            )}
        </div>
    ) : null;

    return (
        <div className="max-w-7xl mx-auto px-0 sm:px-6 py-6 sm:py-8" data-testid="visual-page">
            {/* Header */}
            <div className="flex items-end justify-between mb-6 border-b border-black/20 pb-3">
                <div>
                    <h1 className="font-serif text-4xl text-black tracking-tight">
                        <HoverTip text="Vista visual: un mapa interactivo donde cada empresa de tus niveles se sitúa según su valoración (Ratio Compra/Venta) y calidad cualitativa. Te da una foto global de dónde están las oportunidades de un vistazo.">
                            <span className="cursor-help" data-testid="visual-title-tip">Visual</span>
                        </HoverTip>
                    </h1>
                    <div className="text-xs text-[#4A4A4A] mt-1 font-mono">
                        {selected.size} / {rows.length} empresas seleccionadas · {mapRows.length} visibles en el mapa
                    </div>
                </div>
                <button onClick={reload} className="overline text-xs px-3 py-1.5 border border-black hover:bg-black hover:text-white transition" data-testid="visual-refresh">
                    {loading ? <Loader2 size={12} className="inline mr-1 animate-spin" /> : null}Actualizar
                </button>
            </div>

            {error && (
                <div className="border border-[#B32A22] bg-[#FEF2F2] p-3 text-sm text-[#B32A22] mb-4" data-testid="visual-error">
                    Error: {error}
                </div>
            )}

            {/* Quadrant Map */}
            <div className="border border-black/20 p-4 mb-6 bg-white" data-testid="visual-map">
                <div className="flex items-center justify-between mb-2">
                    <div className="overline text-[#4A4A4A]">Cuadrante calidad ↔ valoración{tlMode && tl ? ` · ${monthLabel(tl.months[tlIdx])}` : ""}</div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setChartFull(true)}
                        className="overline text-xs px-3 py-1.5 border border-black hover:bg-black hover:text-white transition flex items-center gap-1.5"
                        data-testid="visual-expand-chart"
                        title="Ampliar el gráfico a pantalla completa (ideal en móvil / horizontal)"
                    >
                        <Maximize2 size={12} /> Ampliar
                    </button>
                    <button
                        onClick={() => (tlMode ? setTlMode(false) : enableTimeline())}
                        disabled={tlLoading}
                        className={`overline text-xs px-3 py-1.5 border flex items-center gap-1.5 transition ${tlMode ? "bg-[#052049] text-white border-[#052049]" : "border-black hover:bg-black hover:text-white"}`}
                        data-testid="visual-timeline-toggle"
                    >
                        {tlLoading ? <Loader2 size={12} className="animate-spin" /> : <Clock size={12} />}
                        {tlMode ? "Salir de línea de tiempo" : "Línea de tiempo"}
                    </button>
                </div>
                </div>
                {tlErr && <div className="text-xs text-[#B32A22] mb-2 font-mono" data-testid="visual-timeline-error">{tlErr}</div>}
                <div ref={chartRef} className="w-full h-[60vh] min-h-[300px] max-h-[460px] landscape:max-md:h-[85vh] landscape:max-md:max-h-none">
                <ResponsiveContainer width="100%" height="100%">
                    {chartNode}
                </ResponsiveContainer>
                </div>
                {hiddenBar}
                {tlMode && tl && (
                    <div className="mt-3 border-t border-black/10 pt-3" data-testid="visual-timeline-controls">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={onTimelinePlay}
                                className="w-9 h-9 flex items-center justify-center border border-black hover:bg-black hover:text-white transition shrink-0"
                                data-testid="visual-timeline-play"
                                title={recording ? "Detener grabación" : (tlPlaying ? "Pausar" : "Reproducir")}
                            >
                                {recording ? <Square size={14} /> : (tlPlaying ? <Pause size={15} /> : <Play size={15} />)}
                            </button>
                            <input
                                type="range" min={0} max={Math.max(0, tl.months.length - 1)} value={tlIdx}
                                disabled={recording}
                                onChange={(e) => { setTlPlaying(false); setTlIdx(parseInt(e.target.value, 10)); }}
                                className="flex-1 accent-[#052049] cursor-pointer disabled:opacity-50"
                                data-testid="visual-timeline-slider"
                            />
                            <span className="font-mono text-xs text-black w-16 text-right tabular-nums">{monthLabel(tl.months[tlIdx])}</span>
                        </div>
                        <div className="flex items-center justify-between mt-2 gap-3 flex-wrap">
                            <div className="flex items-center gap-3 text-[11px] font-mono text-[#4A4A4A]">
                                <label className="flex items-center gap-1.5 cursor-pointer" title="Muestra el rastro del recorrido de cada empresa y anima el movimiento entre posiciones al mover la barra (en vez de saltar)">
                                    <input type="checkbox" checked={tlTrail} onChange={(e) => setTlTrail(e.target.checked)} data-testid="visual-timeline-trail" />
                                    Estela
                                </label>
                                <label className="flex items-center gap-1.5">
                                    Velocidad
                                    <select value={tlSpeed} onChange={(e) => setTlSpeed(parseInt(e.target.value, 10))} disabled={recording} className="border border-black/30 px-1 py-0.5" data-testid="visual-timeline-speed">
                                        <option value={1000}>Lenta</option>
                                        <option value={600}>Normal</option>
                                        <option value={250}>Rápida</option>
                                    </select>
                                </label>
                            </div>
                            {/* Record / Export / Delete */}
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setRecArmed((a) => !a)}
                                    disabled={recording}
                                    className={`text-xs font-mono px-2.5 py-1.5 border flex items-center gap-1.5 transition disabled:opacity-50 ${recArmed ? "bg-[#B32A22] text-white border-[#B32A22]" : "border-black hover:bg-black hover:text-white"}`}
                                    data-testid="visual-timeline-record"
                                    title={recArmed ? "Grabación activada: pulsa ▶ para grabar el recorrido. Clic para desactivar." : "Activar grabación del próximo recorrido"}
                                >
                                    <Circle size={11} fill={recArmed ? "#fff" : "none"} className={recording ? "animate-pulse" : ""} />
                                    {recording ? "Grabando…" : (recArmed ? "Grabar ●" : "Grabar")}
                                </button>
                                <button
                                    onClick={() => setLibOpen(true)}
                                    disabled={clipCount === 0}
                                    className="text-xs font-mono px-2.5 py-1.5 border border-black hover:bg-black hover:text-white transition flex items-center gap-1.5 disabled:opacity-40"
                                    data-testid="visual-timeline-export"
                                    title="Ver, reproducir, renombrar, descargar y compartir tus recorridos grabados"
                                >
                                    <FolderOpen size={12} /> Exportar
                                    {clipCount > 0 && <span className="bg-[#052049] text-white rounded-full px-1.5 py-0.5 text-[9px] leading-none" data-testid="visual-timeline-clipcount">{clipCount}</span>}
                                </button>
                                {clipCount > 0 && (
                                    <button
                                        onClick={clearClips}
                                        className="text-xs font-mono px-2.5 py-1.5 border border-[#B32A22] text-[#B32A22] hover:bg-[#B32A22] hover:text-white transition flex items-center gap-1.5"
                                        data-testid="visual-timeline-clear"
                                        title="Borrar todos los recorridos guardados (empezar de cero)"
                                    >
                                        <Trash2 size={12} /> Borrar
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="mt-2 text-[10px] italic text-[#7A7A7A]">Izquierda = pasado · derecha = hoy. La valoración histórica se reconstruye proyectando el valor razonable actual; Score/TAM/KPI se congelan hasta que haya snapshots diarios.</div>
                    </div>
                )}
                <div className="text-xs text-[#4A4A4A] mt-3 font-sans flex items-center gap-5 flex-wrap leading-relaxed">
                    {(() => {
                        // Read live thresholds via signalFor probe values so the legend
                        // reflects whatever the user has configured in Umbrales.
                        const t = (typeof window !== "undefined" && JSON.parse(window.localStorage.getItem("vs.thresholds.v1") || "null"))?.venta;
                        const cheap = t?.cheap ?? 20;
                        const fair = t?.fair ?? 0;
                        return (
                            <span>📍 Tamaño = TAM Score · Color = Ratio Venta (
                                <span className="text-[#1D7044] font-semibold">verde ≥{cheap}%</span>,{" "}
                                <span className="text-[#B8860B] font-semibold">ámbar {fair}% a {cheap}%</span>,{" "}
                                <span className="text-[#B32A22] font-semibold">rojo &lt;{fair}%</span>
                                ) · configurable en <em>Umbrales</em>
                            </span>
                        );
                    })()}
                    <span>Líneas discontinuas: <span className="font-mono">mediana</span> de las empresas visibles — el cruce divide los 4 cuadrantes dinámicamente.</span>
                    <span className="basis-full">➤ <strong>Aguja KPI</strong>: dirección = validación operativa (dcha si C&gt;1, izq si C&lt;1; arriba si C&gt;media, abajo si C&lt;media). Longitud/inclinación ∝ fuerza relativa. <span className="text-[#1D7044] font-semibold">verde</span> = valida (arriba-dcha), <span className="text-[#B8860B] font-semibold">ámbar</span> = mixto, <span className="text-[#B32A22] font-semibold">rojo</span> = refuta (abajo-izq). Sin aguja = sin KPI.</span>
                </div>
            </div>

            {/* Filters */}
            <div className="border border-black/20 p-4 mb-4 bg-[#FAF6EE]" data-testid="visual-filters">
                <div className="flex items-center justify-between mb-3">
                    <div className="overline text-[#4A4A4A]">Filtros · marcan/desmarcan empresas en la tabla y las muestran u ocultan en el mapa</div>
                    <button onClick={resetFilters} className="text-xs font-mono text-[#4A4A4A] hover:text-black flex items-center gap-1" data-testid="visual-reset-filters">
                        <RotateCcw size={12} /> Reset
                    </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="flex flex-col gap-1 col-span-2 md:col-span-2" data-testid="filter-level">
                        <label className="overline text-[#4A4A4A]">Nivel</label>
                        <div className="flex border border-black/30 divide-x divide-black/20 w-full">
                            {[
                                { k: "both", label: "Ambas" },
                                { k: "n1", label: "Nivel 1" },
                                { k: "n2", label: "Nivel 2" },
                            ].map((o) => (
                                <button
                                    key={o.k}
                                    type="button"
                                    onClick={() => setFilters({ ...filters, level: o.k })}
                                    className={`min-w-0 basis-0 flex-1 text-center text-[11px] font-mono px-1 py-1.5 whitespace-nowrap transition-colors ${filters.level === o.k ? "bg-black text-[#FDF1E6]" : "bg-white text-black hover:bg-[#F1E9D9]"}`}
                                    data-testid={`filter-level-${o.k}`}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <label className="block" data-testid="filter-sector">
                        <div className="text-[11px] text-[#4A4A4A] font-mono mb-1">Sector</div>
                        <select
                            value={filters.sector}
                            onChange={(e) => setFilters({ ...filters, sector: e.target.value })}
                            className="border border-black px-2 py-1 text-sm font-mono w-full bg-white"
                            data-testid="filter-sector-select"
                        >
                            <option value="all">Todos</option>
                            {sectorOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </label>
                    <FilterField label="Score min" value={filters.score} step={1} onChange={(v) => setFilters({ ...filters, score: v })} testid="filter-score" />
                    <FilterField label="TAM Score min" value={filters.tam} step={0.5} onChange={(v) => setFilters({ ...filters, tam: v })} testid="filter-tam" />
                    <FilterField label="Coef KPI ≥" value={filters.kpi} step={0.1} onChange={(v) => setFilters({ ...filters, kpi: v })} testid="filter-kpi" />
                    <FilterField label="Ratio Compra ≥ %" value={filters.rc} step={5} onChange={(v) => setFilters({ ...filters, rc: v })} testid="filter-rc" suffix="%" />
                    <FilterField label="Ratio Venta ≥ %" value={filters.rv} step={5} onChange={(v) => setFilters({ ...filters, rv: v })} testid="filter-rv" suffix="%" />
                    <FilterField label="Combinado cual. ≥ %" value={filters.cqual} step={5} onChange={(v) => setFilters({ ...filters, cqual: v })} testid="filter-cqual" suffix="%" />
                    <FilterField label="Combinado total ≥ %" value={filters.ctotal} step={5} onChange={(v) => setFilters({ ...filters, ctotal: v })} testid="filter-ctotal" suffix="%" />
                </div>
            </div>

            {/* Table */}
            <div className="border border-black/20 bg-white overflow-x-auto" data-testid="visual-table">
                <table className="w-full text-sm font-mono">
                    <thead className="bg-black text-white">
                        <tr>
                            <th className="p-2 text-left w-[29px] sticky left-0 z-30 bg-black">
                                <input type="checkbox" checked={allSelected} onChange={toggleAll} className="cursor-pointer" data-testid="visual-toggle-all" />
                            </th>
                            <SortableTh label="Ticker" k="ticker" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="left" className="sticky left-[29px] z-30 bg-black" />
                            <SortableTh label="Nombre" k="name" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="left" className="font-sans" />
                            <SortableTh label="Tesis" k="thesis_updated_at" sortKey={sortKey} sortDir={sortDir} onSort={onSort} tip="Días desde la última generación de la tesis, de cualquier usuario: la generación se comparte entre todos los que siguen la empresa. En rojo si la empresa ha publicado resultados posteriores (conviene regenerar); en gris, sin enlace, durante los 3 días siguientes a una generación (no se puede regenerar todavía, salvo tras resultados). El nombre de la empresa sale en rojo, con los días sin actualizar, cuando hay una generación más nueva que tu tesis." />
                            <SortableTh label={<span className="flex flex-col leading-tight items-end"><span>Ant.</span><span>result.</span></span>} k="last_earnings_date" sortKey={sortKey} sortDir={sortDir} onSort={onSort} tip="Fecha de los últimos resultados publicados (Yahoo Finance)." />
                            <SortableTh label={<span className="flex flex-col leading-tight items-end"><span>Próx.</span><span>result.</span></span>} k="next_earnings_date" sortKey={sortKey} sortDir={sortDir} onSort={onSort} tip="Fecha estimada de los próximos resultados (Yahoo Finance). En rojo si faltan 7 días o menos. El símbolo ≈ indica que la fecha es tentativa." />
                            <SortableTh label="Score" k="avg_overall_score" sortKey={sortKey} sortDir={sortDir} onSort={onSort} tip={TIP.score} />
                            <SortableTh label="TAM Score" k="sum_tam_score" sortKey={sortKey} sortDir={sortDir} onSort={onSort} tip={TIP.tam} />
                            <SortableTh label={<span className="flex flex-col leading-tight items-end"><span>Coef</span><span>KPI</span></span>} k="kpi_coef" sortKey={sortKey} sortDir={sortDir} onSort={onSort} tip={TIP.kpi} />
                            <SortableTh label={<span className="flex flex-col leading-tight items-end"><span>Combinado</span><span>cualitativo</span></span>} k="combined_qual" sortKey={sortKey} sortDir={sortDir} onSort={onSort} tip={TIP.combined_qual} />
                            <SortableTh label="Compra %" k="ratio_compra_pct" sortKey={sortKey} sortDir={sortDir} onSort={onSort} tip={TIP.compra} />
                            <SortableTh label="Venta %" k="ratio_venta_pct" sortKey={sortKey} sortDir={sortDir} onSort={onSort} tip={TIP.venta} />
                            <SortableTh label={<span className="flex flex-col leading-tight items-end"><span>Combinado</span><span>total</span></span>} k="combined" sortKey={sortKey} sortDir={sortDir} onSort={onSort} tip={TIP.combined} />
                        </tr>
                    </thead>
                    <tbody>
                        {sortedRows.length === 0 && !loading && (
                            <tr><td colSpan={13} className="p-6 text-center text-[#4A4A4A] font-sans">No hay empresas miembros de tesis trend. Genera tesis primero.</td></tr>
                        )}
                        {sortedRows.map((r, i) => {
                            const checked = selected.has(r.ticker);
                            const incomplete = r.ratio_compra_pct == null || r.avg_overall_score == null;
                            // Tesis column: days since the company's last generation (shared by
                            // every user who follows it; falls back to the user's own thesis).
                            const genAt = r.thesis_generated_at || r.thesis_updated_at;
                            const stale = freshnessInfo(genAt, r.last_earnings_date, r.next_earnings_date)?.stale;
                            const regenBlocked = !!r.thesis_regen_blocked && !stale;
                            const pending = !!r.thesis_pending;
                            // Mark the boundary where the selected group (floated to the top by
                            // sortedRows) ends and the unselected rest begins.
                            const groupBoundary = !checked && i > 0 && selected.has(sortedRows[i - 1].ticker);
                            return (
                                <tr key={r.ticker} className={`border-t ${groupBoundary ? "border-t-2 border-t-black/40" : "border-black/10"} ${incomplete ? "text-[#9ca3af]" : "hover:bg-[#FAF6EE]"}`} data-testid={`visual-row-${r.ticker}`}>
                                    <td className="p-2 w-[29px] sticky left-0 z-20 bg-white"><input type="checkbox" checked={checked} onChange={() => toggleOne(r.ticker)} className="cursor-pointer" data-testid={`visual-toggle-${r.ticker}`} /></td>
                                    <td className="p-2 font-semibold sticky left-[29px] z-20 bg-white border-r border-black/10"><HoverTip text={`Abrir la ficha de valoración de ${r.name || r.ticker} (${r.ticker}): precio, Ratio Compra/Venta, proyecciones e histórico.`}><Link to={`/company/${r.ticker}`} className="hover:underline" data-testid={`visual-ticker-link-${r.ticker}`}>{r.ticker}</Link></HoverTip></td>
                                    <td className="p-2 font-sans text-xs"><span className="inline-flex items-start gap-1.5">
                                        <span className="flex flex-col min-w-0">
                                            {r.company_thesis_id
                                                ? <HoverTip text={pending ? `Hay una generación nueva de ${r.name || r.ticker} hecha por otro usuario: abre su tesis y pulsa «Actualizar» para traerla a tu cuenta (unos segundos, sin coste).` : `Ver la tesis de ${r.name || r.ticker}: el plan de la empresa y sus motores (drivers).`}>
                                                    <Link to={`/thesis/${r.company_thesis_id}`} className="hover:underline" style={pending ? { color: "#B32A22", fontWeight: 700 } : undefined} data-testid={`visual-name-link-${r.ticker}`}>{r.name}</Link>
                                                  </HoverTip>
                                                : <HoverTip text={`${r.name || r.ticker}: aún no tienes su tesis de empresa (plan), así que no hay enlace. Aparece aquí por formar parte de tus tesis de tendencia.`}>
                                                    <span className="cursor-help" style={pending ? { color: "#B32A22", fontWeight: 700 } : undefined}>{r.name}</span>
                                                  </HoverTip>}
                                            {pending && r.company_thesis_id && (
                                                <HoverTip text="Otro usuario ha generado esta empresa después de tu tesis. Actualizar la trae a tu cuenta en unos segundos, sin coste.">
                                                    <Link to={`/thesis/${r.company_thesis_id}`} className="text-[10px] leading-tight hover:underline whitespace-nowrap" style={{ color: "#B32A22" }} data-testid={`visual-pending-${r.ticker}`}>
                                                        {r.thesis_pending_days ?? 0}d sin actualizar
                                                    </Link>
                                                </HoverTip>
                                            )}
                                        </span>
                                        <AlertBell ticker={r.ticker} alert={alerts[r.ticker]} onSaved={onAlertSaved} /></span></td>
                                    <td className="p-2 text-right">{regenBlocked ? (
                                        <HoverTip text={`Días desde la última generación de la tesis (compartida por todos los usuarios que siguen la empresa). En gris: se generó hace menos de 3 días y no ha dado tiempo material a que cambie nada, así que nadie puede regenerarla hasta dentro de ${r.thesis_regen_wait_days || 1} día${(r.thesis_regen_wait_days || 1) === 1 ? "" : "s"}, salvo que publique resultados (entonces pasa a rojo).`}>
                                            <span className="font-mono tabular-nums cursor-help text-[#9ca3af]" data-testid={`visual-fresh-${r.ticker}`}>{freshnessInfo(genAt)?.days ?? 0}d</span>
                                        </HoverTip>
                                    ) : (
                                        <Link to={`/thesis?company=${encodeURIComponent(r.ticker)}${stale ? "&regen=1" : ""}`} className="hover:underline" title="Regenerar la tesis" data-testid={`visual-fresh-link-${r.ticker}`}><FreshnessBadge updatedAt={genAt} lastEarningsDate={r.last_earnings_date} nextEarningsDate={r.next_earnings_date} noun="la última generación de la tesis (compartida por todos los usuarios que siguen la empresa)" action="regenerar" extraTip=" Pulsa para regenerarla." testid={`visual-fresh-${r.ticker}`} className="!cursor-pointer" /></Link>
                                    )}</td>
                                    <td className="p-2 text-right"><LastEarningsBadge date={r.last_earnings_date} testid={`visual-last-earnings-${r.ticker}`} /></td>
                                    <td className="p-2 text-right"><EarningsBadge date={r.next_earnings_date} estimated={r.next_earnings_estimated} testid={`visual-earnings-${r.ticker}`} /></td>
                                    <td className="p-2 text-right cursor-pointer hover:underline" title="Ver histórico" onClick={() => setHistoryRow({ ticker: r.ticker, name: r.name })} data-testid={`visual-score-${r.ticker}`}>{fmtN(r.avg_overall_score)}</td>
                                    <td className="p-2 text-right cursor-pointer hover:underline" title="Ver histórico" onClick={() => setHistoryRow({ ticker: r.ticker, name: r.name })} data-testid={`visual-tam-${r.ticker}`}>{fmtN(r.sum_tam_score, 2)}</td>
                                    <td className="p-2 text-right cursor-pointer hover:underline" title="Ver histórico" onClick={() => setHistoryRow({ ticker: r.ticker, name: r.name })} data-testid={`visual-kpi-${r.ticker}`}>
                                        {typeof r.kpi_coef === "number"
                                            ? <span style={{ color: coefColor(r.kpi_coef) }} className="font-semibold">{r.kpi_coef.toFixed(2)}</span>
                                            : <span className="text-[#9ca3af]">—</span>}
                                    </td>
                                    <td className="p-2 text-right">{(r.combined_qual * 100).toFixed(1)}%</td>
                                    <td className="p-2 text-right cursor-pointer hover:underline" style={{ color: signalFor(r.ratio_compra_pct, "compra").color }} title="Ver histórico" onClick={() => setHistoryRow({ ticker: r.ticker, name: r.name })} data-testid={`visual-rc-${r.ticker}`}>{fmtPct(r.ratio_compra_pct)}</td>
                                    <td className="p-2 text-right cursor-pointer hover:underline" style={{ color: signalFor(r.ratio_venta_pct, "venta").color }} title="Ver histórico" onClick={() => setHistoryRow({ ticker: r.ticker, name: r.name })} data-testid={`visual-rv-${r.ticker}`}>{fmtPct(r.ratio_venta_pct)}</td>
                                    <td className="p-2 text-right font-semibold">{(r.combined * 100).toFixed(1)}%</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* KPI notice */}
            <div className="text-xs text-[#4A4A4A] mt-3 font-sans leading-relaxed" data-testid="visual-kpi-note">
                {kpiMean != null && (
                    <span>El <strong>coef KPI</strong> modula los combinados respecto a la media del universo (<span className="font-mono">{kpiMean.toFixed(2)}</span>): por encima del punto neutro mejora el porcentaje, por debajo lo empeora (cuenta una sola vez). </span>
                )}
                {noKpiCount > 0 && (
                    <span data-testid="visual-no-kpi-note">
                        {noKpiCount === 1 ? "1 empresa aún no tiene" : `${noKpiCount} empresas aún no tienen`} coeficiente KPI («—»): no se incluye este factor en {noKpiCount === 1 ? "su combinado" : "sus combinados"}. Analíza{noKpiCount === 1 ? "la" : "las"} en <Link to="/kpis" className="underline hover:text-black">/kpis</Link>.
                    </span>
                )}
            </div>

            <ExportLibrary
                open={libOpen}
                onClose={async () => { setLibOpen(false); setClipCount(await countMediaItems("timeline-clip")); }}
                kind="timeline-clip"
                title="Recorridos grabados"
            />

            {chartFull && (
                <div className="fixed inset-0 z-[70] bg-white flex flex-col" data-testid="visual-chart-fullscreen" style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
                    <div className="flex items-center justify-between px-4 py-2 border-b border-black shrink-0">
                        <div className="overline text-[#4A4A4A] truncate min-w-0 flex-1 mr-2">Cuadrante calidad ↔ valoración{tlMode && tl ? ` · ${monthLabel(tl.months[tlIdx])}` : ""}</div>
                        <div className="flex items-center gap-2 shrink-0">
                            <ShareMenu size="md" title="Cuadrante VegaValor" testidPrefix="visual-chart-share"
                                createShare={async () => shareUpload(await getSvgJpgBlob(fullChartRef.current), "jpg", "Cuadrante VegaValor")} />
                            <button onClick={downloadJpg} disabled={jpgBusy} className="overline text-xs px-3 py-1.5 border border-black hover:bg-black hover:text-white transition flex items-center gap-1.5 disabled:opacity-50" data-testid="visual-chart-download-jpg">
                                {jpgBusy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} JPG
                            </button>
                            <button onClick={() => setChartFull(false)} className="overline text-xs px-3 py-1.5 border border-black hover:bg-black hover:text-white transition flex items-center gap-1.5" data-testid="visual-chart-fullscreen-close">
                                <X size={14} /> Cerrar
                            </button>
                        </div>
                    </div>
                    <div ref={fullChartRef} className="flex-1 min-h-0 px-1 sm:px-2 pt-1 sm:pt-2 pb-0">
                        <PinchZoomPane className="w-full h-full">
                            <ResponsiveContainer width="100%" height="100%">
                                {chartNode}
                            </ResponsiveContainer>
                        </PinchZoomPane>
                    </div>
                    {hiddenBar && <div className="px-3 shrink-0">{hiddenBar}</div>}
                    <div className="text-[10px] text-[#7A7A7A] px-4 py-0.5 -mt-1 text-center shrink-0">Pellizca para hacer zoom · arrastra para desplazar · doble toque para restablecer · gira el móvil para más ancho</div>
                </div>
            )}

            {historyRow && <VisualHistoryModal ticker={historyRow.ticker} name={historyRow.name} onClose={() => setHistoryRow(null)} />}
        </div>
    );
}

// ---------- Classic per-company history chart (opened from any of the 5 columns) ----------
// 4 Y-axes: Score, TAM Score and Coef KPI each get their own (3 qualitative axes), and
// Ratio Compra/Venta % share a 4th (both are percentages on the same scale).
// Colors picked to stay legible both on the white chart canvas AND the near-black
// tooltip background (the original navy/dark-green were unreadable on the latter).
const VISUAL_METRIC_META = {
    score: { label: "Score", color: "#3B9EFF", fmt: (v) => fmtN(v, 1), axis: "score" },
    tam: { label: "TAM Score", color: "#2FA86B", fmt: (v) => fmtN(v, 2), axis: "tam" },
    kpi_coef: { label: "Coef KPI", color: "#B8860B", fmt: (v) => fmtN(v, 2), axis: "kpi" },
    rc: { label: "Ratio Compra %", color: "#8B5CF6", fmt: (v) => fmtPct(v), axis: "ratio" },
    rv: { label: "Ratio Venta %", color: "#B32A22", fmt: (v) => fmtPct(v), axis: "ratio" },
    price: { label: "Precio", color: "#EA7317", fmt: (v) => fmtN(v, 2), axis: "price" },
};
// Price last: lets you read straight across whether a ratio's shape (Ratio Compra/Venta,
// drawn just before) tracks the price curve (same shape → price-driven) or diverges from
// it (different shape → the underlying fundamentals/target moved, not just the market price).
const VISUAL_METRIC_ORDER = ["score", "tam", "kpi_coef", "rc", "rv", "price"];
const VISUAL_AXES = [
    { id: "score", orientation: "left", color: "#3B9EFF", fmt: (v) => v.toFixed(0) },
    { id: "tam", orientation: "left", color: "#2FA86B", fmt: (v) => v.toFixed(2) },
    { id: "kpi", orientation: "right", color: "#B8860B", fmt: (v) => v.toFixed(2) },
    { id: "ratio", orientation: "right", color: "#8B5CF6", fmt: (v) => `${v.toFixed(0)}%` },
    { id: "price", orientation: "right", color: "#EA7317", fmt: (v) => v.toFixed(0) },
];

// Pad a metric's real min/max so its line doesn't hug the axis edges (and stays
// readable even when flat) — NOT normalized to a shared scale, each axis keeps its
// own real units; a domain shared by 2 metrics (the ratio axis) is passed pre-merged.
//
// Every axis shares the same plot height, so with plain symmetric padding [min-pad,
// max+pad], ANY series (single point OR a straight min→max line, which is exactly what
// a 2-point series looks like) lands its data at the SAME relative vertical band
// regardless of which axis owns it or what its real values are — whichever metric draws
// last (Coef KPI, then the ratios) visually sits on top and hides the ones drawn earlier
// (Score, TAM), because their lines trace literally the same pixels. `targetFrac` fixes
// this by anchoring each axis's data at a DIFFERENT vertical fraction of its own domain
// (0.7 for score, 0.4 for tam, 0.6 for kpi, 0.5 for the shared ratio axis) — applied
// uniformly whether the series has one point or many, so two metrics never coincide
// just because they happen to both be flat or both be a simple rise/fall.
const paddedDomain = (vals, targetFrac = 0.5, padFrac = 0.15) => {
    if (!vals.length) return [0, 1];
    const min = Math.min(...vals), max = Math.max(...vals);
    const center = (min + max) / 2;
    const halfSpan = max > min ? (max - min) / 2 : (Math.abs(center) || 1) * 0.1;
    // k = the tighter of the two margins around targetFrac; H is sized so the real
    // [min,max] band always fits inside the final domain with padFrac of extra room.
    const k = Math.min(targetFrac, 1 - targetFrac);
    const H = (halfSpan / k) * (1 + padFrac);
    return [center - H * targetFrac, center + H * (1 - targetFrac)];
};

const VisualHistoryTip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    return (
        <div className="bg-[#111111] text-white text-[11px] p-2 border border-black" data-testid="visual-history-tooltip">
            <div className="font-semibold mb-1">{dayLabel(label)}</div>
            {VISUAL_METRIC_ORDER.map((m) => {
                const v = row[m];
                if (v == null) return null;
                const meta = VISUAL_METRIC_META[m];
                return <div key={m} style={{ color: meta.color }} className="tabular-nums">{meta.label}: {meta.fmt(v)}</div>;
            })}
        </div>
    );
};

const VisualHistoryModal = ({ ticker, name, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hidden, setHidden] = useState(new Set());
    const [busy, setBusy] = useState(false);
    const ref = useRef(null);
    const pinchRef = useRef(null);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        thesisVisualHistory(ticker).then((d) => { if (alive) setData(d); })
            .catch(() => { if (alive) setData(null); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [ticker]);

    // Only metrics with at least one recorded/anchored point get an axis + line. An
    // axis stacked with a degenerate [0,1] placeholder domain (from a metric with zero
    // data) was corrupting the neighboring same-side axis's rendering — so metrics
    // without data are left out entirely instead of rendered empty.
    //
    // `live` (the metric's current value, independent of the events-series anchoring
    // done server-side) is used as a fallback: if a metric's series ever comes back
    // empty despite a real live value existing, synthesize today's point from it
    // instead of hiding a metric the user can see has real data.
    const { rows, domains, tsDomain, activeMetrics, emptyMetrics } = useMemo(() => {
        if (!data?.series) return { rows: [], domains: {}, tsDomain: [0, 1], activeMetrics: [], emptyMetrics: [] };
        const today = new Date().toISOString().slice(0, 10);
        const effectiveSeries = {};
        for (const m of VISUAL_METRIC_ORDER) {
            const pts = data.series[m] || [];
            const liveVal = data.live?.[m];
            if (!pts.length && liveVal != null) {
                effectiveSeries[m] = [{ date: today, value: liveVal }];
            } else {
                effectiveSeries[m] = pts;
            }
        }
        // Window the chart to exactly [earliest qualitative point, today] — not the full
        // ratio history: Score/TAM/Coef KPI only exist from real reanalyses, so that span
        // is what the chart is actually about. Price and Ratio Compra/Venta get backfilled
        // for up to 24 months of price history (see backend), which would otherwise
        // stretch the axis far past the real analysis window and squash every other
        // metric into a narrow sliver at the end — so ALL metrics share this same window,
        // price included, keeping every line on the same time scale.
        const qualTs = ["score", "tam", "kpi_coef"].flatMap((m) => (effectiveSeries[m] || []).map((p) => new Date(p.date).getTime()));
        const nowTs = Date.now();
        let windowMin = -Infinity, windowMax = Infinity;
        if (qualTs.length) {
            // Floored to 00:00 UTC of the first analysis day so that day's daily close
            // (dated at midnight) is included rather than dropped for predating the
            // exact timestamp of the first reanalysis.
            const firstDay = new Date(Math.min(...qualTs));
            windowMin = Date.UTC(firstDay.getUTCFullYear(), firstDay.getUTCMonth(), firstDay.getUTCDate());
            windowMax = nowTs;
            for (const m of VISUAL_METRIC_ORDER) {
                effectiveSeries[m] = (effectiveSeries[m] || []).filter((p) => {
                    const t = new Date(p.date).getTime();
                    return t >= windowMin && t <= windowMax;
                });
            }
        }
        const activeMetrics = VISUAL_METRIC_ORDER.filter((m) => effectiveSeries[m].length > 0);
        const emptyMetrics = VISUAL_METRIC_ORDER.filter((m) => !effectiveSeries[m].length);
        const dateSet = new Set();
        for (const m of activeMetrics) for (const p of effectiveSeries[m]) dateSet.add(p.date);
        const byDateMetric = {};
        for (const m of activeMetrics) {
            byDateMetric[m] = {};
            for (const p of effectiveSeries[m]) byDateMetric[m][p.date] = p.value;
        }
        const rows = Array.from(dateSet).sort().map((d) => {
            const row = { date: d, ts: new Date(d).getTime() };
            for (const m of activeMetrics) row[m] = byDateMetric[m][d] ?? null;
            return row;
        });
        const valsOf = (metrics) => metrics.flatMap((m) => effectiveSeries[m].map((p) => p.value));
        const activeAxisIds = new Set(activeMetrics.map((m) => VISUAL_METRIC_META[m].axis));
        const domains = {};
        if (activeAxisIds.has("score")) domains.score = paddedDomain(valsOf(["score"]), 0.7);
        if (activeAxisIds.has("tam")) domains.tam = paddedDomain(valsOf(["tam"]), 0.4);
        if (activeAxisIds.has("kpi")) domains.kpi = paddedDomain(valsOf(["kpi_coef"]), 0.6);
        if (activeAxisIds.has("ratio")) domains.ratio = paddedDomain(valsOf(["rc", "rv"]), 0.5);
        if (activeAxisIds.has("price")) domains.price = paddedDomain(valsOf(["price"]), 0.3);
        // Real time-scale X domain: [earliest qualitative point, last actual data point]
        // when qualitative data exists (the chart is windowed to that same start above),
        // else the real data range. The right edge is always the latest point actually
        // PLOTTED — never today — so the axis doesn't stretch past the real data into
        // empty trailing space when the last reanalysis/price point is somewhat old. No
        // padding on either side: the axis spans exactly the analysed period, never
        // before it or into the future. Only a degenerate single-instant range gets a
        // ±12h spread so the axis doesn't collapse to zero width.
        const tsVals = rows.map((r) => r.ts);
        const rawMin = qualTs.length ? windowMin : (tsVals.length ? Math.min(...tsVals) : Date.now());
        const rawMax = tsVals.length ? Math.max(...tsVals) : Date.now();
        const tsDomain = rawMax > rawMin ? [rawMin, rawMax] : [rawMin - 43200000, rawMax + 43200000];
        return { rows, domains, tsDomain, activeMetrics, emptyMetrics };
    }, [data]);
    const activeAxes = VISUAL_AXES.filter((ax) => domains[ax.id]);

    const toggle = (m) => setHidden((h) => { const n = new Set(h); n.has(m) ? n.delete(m) : n.add(m); return n; });

    const dl = async () => {
        setBusy(true);
        try { await downloadSvgJpg(ref.current, `historico-${ticker}`); }
        catch { toast.error("No se pudo exportar el gráfico"); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="visual-history-modal" style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
            <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-black shrink-0">
                <h2 className="font-serif text-base sm:text-2xl text-[#052049] flex items-center gap-1.5 min-w-0">
                    <LineChartIcon size={20} className="shrink-0" /> <span className="truncate">{name || ticker} · Histórico</span>
                    {rows.length > 0 && (
                        <HoverTip text={`5 ejes Y, cada uno con su color: Score y TAM Score a la izquierda, Coef KPI, Ratio Compra/Venta % (comparten eje, misma unidad) y Precio a la derecha. Cada eje se ajusta automáticamente al rango real de sus datos para que las líneas ocupen un espacio similar y sean legibles. Score/TAM/Coef KPI se registran solo cuando cambian (al reanalizar tesis/KPI), así que solo tienen historial real desde la primera vez que se calcularon. Ratio Compra/Venta se completan también hacia atrás proyectando tu objetivo actual sobre el cierre mensual, y de aquí en adelante se registran el día 1 y el 15 de cada mes; Precio usa el cierre diario. Todas las series se muestran en el mismo periodo: desde el primer análisis hasta hoy. Compara la FORMA de Precio contra Ratio Compra/Venta: si trazan la misma curva, el movimiento del ratio lo explica el precio de mercado; si divergen, lo explican los fundamentales (objetivo POC/POV cambiado). Haz clic en la leyenda para mostrar/ocultar una serie.`} maxWidth={340}>
                            <button className="text-[#9A9A9A] hover:text-[#052049] shrink-0" data-testid="visual-history-info" aria-label="Más información"><Info size={15} /></button>
                        </HoverTip>
                    )}
                </h2>
                <div className="flex items-center gap-3 shrink-0">
                    {rows.length > 0 && (
                        <div className="hidden sm:flex items-center gap-2 border-r border-black/10 pr-3 mr-0.5">
                            <button onClick={() => pinchRef.current?.zoomOut()} className="text-[#7A7A7A] hover:text-[#052049]" title="Alejar" data-testid="visual-history-zoom-out"><ZoomOut size={16} /></button>
                            <button onClick={() => pinchRef.current?.zoomIn()} className="text-[#7A7A7A] hover:text-[#052049]" title="Acercar" data-testid="visual-history-zoom-in"><ZoomIn size={16} /></button>
                            <button onClick={() => pinchRef.current?.reset()} className="text-[#7A7A7A] hover:text-[#052049]" title="Restablecer zoom" data-testid="visual-history-zoom-reset"><RotateCcw size={16} /></button>
                        </div>
                    )}
                    <ShareMenu size="md" title={`Histórico ${ticker} · VegaValor`} testidPrefix="visual-history-share"
                        createShare={async () => shareUpload(await getSvgJpgBlob(ref.current), "jpg", `Histórico ${ticker}`)} />
                    <button onClick={dl} disabled={busy} className="text-[#7A7A7A] hover:text-[#052049] inline-flex items-center gap-1 text-xs uppercase tracking-wide disabled:opacity-50" data-testid="visual-history-download-jpg" title="Descargar en JPG">
                        {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} JPG
                    </button>
                    <button onClick={onClose} className="text-[#7A7A7A] hover:text-[#052049]" data-testid="visual-history-modal-close" aria-label="Cerrar"><X size={20} /></button>
                </div>
            </div>
            {rows.length > 0 && (
                <div className="flex sm:hidden items-center justify-center gap-4 px-4 py-1 border-b border-black/10 shrink-0">
                    <button onClick={() => pinchRef.current?.zoomOut()} className="text-[#7A7A7A] hover:text-[#052049]" title="Alejar" data-testid="visual-history-zoom-out-mobile"><ZoomOut size={16} /></button>
                    <button onClick={() => pinchRef.current?.zoomIn()} className="text-[#7A7A7A] hover:text-[#052049]" title="Acercar" data-testid="visual-history-zoom-in-mobile"><ZoomIn size={16} /></button>
                    <button onClick={() => pinchRef.current?.reset()} className="text-[#7A7A7A] hover:text-[#052049]" title="Restablecer zoom" data-testid="visual-history-zoom-reset-mobile"><RotateCcw size={16} /></button>
                </div>
            )}
            {loading ? (
                <div className="flex-1 flex items-center justify-center text-[#9A9A9A]"><Loader2 className="animate-spin" size={24} /></div>
            ) : rows.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-[#9A9A9A] text-sm" data-testid="visual-history-empty">Sin histórico todavía para {ticker}.</div>
            ) : (
                <>
                    <div ref={ref} className="flex-1 min-h-0 px-1 sm:px-2 pt-2">
                        <PinchZoomPane className="w-full h-full" ref={pinchRef} hideControls>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={rows} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                                    <CartesianGrid stroke="#00000010" vertical={false} />
                                    <XAxis dataKey="ts" type="number" domain={tsDomain}
                                        tickFormatter={dayLabel} tick={{ fontSize: 11, fill: "#7A7A7A" }} minTickGap={40} />
                                    {activeAxes.map((ax) => (
                                        <YAxis key={ax.id} yAxisId={ax.id} orientation={ax.orientation}
                                            domain={domains[ax.id]}
                                            tick={{ fontSize: 10, fill: ax.color }} axisLine={{ stroke: ax.color }} tickLine={false}
                                            width={44} tickFormatter={ax.fmt} />
                                    ))}
                                    <Tooltip content={<VisualHistoryTip />} />
                                    <Legend onClick={(o) => toggle(o.dataKey)} wrapperStyle={{ fontSize: 11, cursor: "pointer" }} />
                                    {activeMetrics.map((m) => (
                                        <Line key={m} yAxisId={VISUAL_METRIC_META[m].axis} type="monotone" dataKey={m} name={VISUAL_METRIC_META[m].label}
                                            stroke={VISUAL_METRIC_META[m].color} strokeWidth={2} dot={{ r: m === "price" ? 1.5 : 3 }} connectNulls
                                            hide={hidden.has(m)} isAnimationActive={false} />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </PinchZoomPane>
                    </div>
                    {emptyMetrics.length > 0 && (
                        <p className="text-[11px] text-[#B8860B] px-4 py-1 border-t border-black/10 shrink-0" data-testid="visual-history-no-data-note">
                            Aún sin ningún punto registrado: {emptyMetrics.map((m) => VISUAL_METRIC_META[m].label).join(", ")}.
                        </p>
                    )}
                    <div className="text-[10px] text-[#7A7A7A] px-4 py-0.5 text-center shrink-0 border-t border-black/10">Pellizca o usa los botones para hacer zoom · arrastra para desplazar · doble toque para restablecer</div>
                </>
            )}
        </div>
    );
};

// ---------- Small subcomponents ----------
const AlertBell = ({ ticker, alert, onSaved }) => {
    const initFrom = (a) => ({
        score: { enabled: a?.score?.enabled || false, dir: a?.score?.dir || "gte", value: a?.score?.value ?? "" },
        tam: { enabled: a?.tam?.enabled || false, dir: a?.tam?.dir || "gte", value: a?.tam?.value ?? "" },
        kpi: { enabled: a?.kpi?.enabled || false, dir: a?.kpi?.dir || "gte", value: a?.kpi?.value ?? "" },
    });
    const active = !!alert;
    const btnRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const [form, setForm] = useState(initFrom(alert));
    const [saving, setSaving] = useState(false);
    useEffect(() => { setForm(initFrom(alert)); }, [alert]);  // eslint-disable-line react-hooks/exhaustive-deps

    const openPanel = () => {
        const r = btnRef.current?.getBoundingClientRect();
        if (r) setPos({ top: r.bottom + 4, left: Math.max(8, Math.min(r.left, window.innerWidth - 280)) });
        setOpen((o) => !o);
    };
    const setMetric = (k, patch) => setForm((f) => ({ ...f, [k]: { ...f[k], ...patch } }));

    const save = async () => {
        setSaving(true);
        try {
            const payload = {};
            for (const k of ["score", "tam", "kpi"]) {
                const m = form[k];
                payload[k] = { enabled: !!m.enabled, dir: m.dir, value: m.value === "" ? null : parseFloat(m.value) };
            }
            const res = await alertSave(ticker, payload);
            onSaved(ticker, res.alert || null);
            toast.success(res.removed ? `Alerta de ${ticker} eliminada` : `Alerta de ${ticker} guardada`);
            setOpen(false);
        } catch { toast.error("No se pudo guardar la alerta"); }
        finally { setSaving(false); }
    };
    const remove = async () => {
        setSaving(true);
        try { await alertDelete(ticker); onSaved(ticker, null); toast.success(`Alerta de ${ticker} eliminada`); setOpen(false); }
        catch { toast.error("No se pudo eliminar"); }
        finally { setSaving(false); }
    };

    return (
        <>
            <button ref={btnRef} onClick={openPanel} className={active ? "text-[#B8860B]" : "text-[#B0B0B0] hover:text-[#052049]"} title={active ? "Alerta configurada — clic para editar" : "Configurar alerta de seguimiento"} data-testid={`alert-bell-${ticker}`}>
                {active ? <BellRing size={15} /> : <Bell size={15} />}
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <div className="fixed z-50 w-64 bg-white border-2 border-[#052049] shadow-xl p-3 text-left font-sans" style={{ top: pos.top, left: pos.left }} data-testid={`alert-panel-${ticker}`}>
                        <div className="text-xs font-semibold text-[#052049] mb-2">Alerta · {ticker}</div>
                        {[["score", "Score"], ["tam", "TAM Score"], ["kpi", "Coef KPI"]].map(([k, label]) => (
                            <div key={k} className="mb-2 border-b border-black/5 pb-2">
                                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                                    <input type="checkbox" checked={form[k].enabled} onChange={(e) => setMetric(k, { enabled: e.target.checked })} data-testid={`alert-${k}-enabled-${ticker}`} />
                                    <span className="font-medium">{label}</span>
                                </label>
                                {form[k].enabled && (
                                    <div className="flex items-center gap-1 mt-1.5 pl-5">
                                        <select value={form[k].dir} onChange={(e) => setMetric(k, { dir: e.target.value })} className="border border-black/30 text-xs px-1 py-0.5" data-testid={`alert-${k}-dir-${ticker}`}>
                                            <option value="gte">≥</option>
                                            <option value="lte">≤</option>
                                        </select>
                                        <input type="number" step="0.1" value={form[k].value} onChange={(e) => setMetric(k, { value: e.target.value })} placeholder="valor" className="border border-black/30 text-xs px-1.5 py-0.5 w-full font-mono" data-testid={`alert-${k}-value-${ticker}`} />
                                    </div>
                                )}
                            </div>
                        ))}
                        <div className="text-[10px] text-[#7A7A7A] mb-2">Además te avisaré si cruza de barato↔caro. Todo en un único email diario.</div>
                        <div className="flex gap-2">
                            <button onClick={save} disabled={saving} className="flex-1 bg-[#052049] text-white text-xs py-1.5 font-semibold disabled:opacity-50" data-testid={`alert-save-${ticker}`}>Guardar</button>
                            {active && <button onClick={remove} disabled={saving} className="px-2 border border-[#B32A22] text-[#B32A22] text-xs py-1.5" data-testid={`alert-remove-${ticker}`}>Quitar</button>}
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

const FilterField = ({ label, value, step, onChange, testid, suffix }) => (
    <label className="block">
        <div className="text-[11px] text-[#4A4A4A] font-mono mb-1">{label}</div>
        <div className="flex items-center">
            <input
                type="number"
                value={value}
                step={step}
                onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                className="border border-black px-2 py-1 text-sm font-mono w-full"
                data-testid={testid}
            />
            {suffix && <span className="ml-1 text-xs text-[#4A4A4A] font-mono">{suffix}</span>}
        </div>
    </label>
);

const SortableTh = ({ label, k, sortKey, sortDir, onSort, tip, align = "right", className = "" }) => {
    const active = sortKey === k;
    const inner = (
        <span className="inline-flex items-center gap-1">
            {label}
            {active ? (sortDir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />) : null}
        </span>
    );
    return (
        <th className={`p-2 cursor-pointer select-none ${align === "left" ? "text-left" : "text-right"} ${className}`} onClick={() => onSort(k)} data-testid={`sort-${k}`}>
            {tip ? <HoverTip text={tip} maxWidth={300}><span className="cursor-help">{inner}</span></HoverTip> : inner}
        </th>
    );
};
